import React, { useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Building2, User } from 'lucide-react';

import { useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { FieldCheckbox, NamedLink } from '../../../components';

import css from './CustomSignupForm.module.css';

const inputCls =
  'w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const FieldInput = ({ name, id, type = 'text', placeholder, validate, autoComplete, hint }) => (
  <Field name={name} validate={validate}>
    {({ input, meta }) => (
      <>
        <input
          {...input}
          id={id}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={inputCls}
        />
        {meta.touched && meta.error ? (
          <p className="mt-1 text-xs text-destructive">{meta.error}</p>
        ) : hint && meta.active ? (
          <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>
        ) : null}
      </>
    )}
  </Field>
);

/**
 * CustomSignupForm.
 * Individual/Business tabs, name/email/password fields.
 *
 * Props (same interface as original SignupForm):
 *   onSubmit, inProgress, termsAndConditions
 */
const CustomSignupForm = ({ onSubmit, inProgress, termsAndConditions, initialValues, onCaptureValues }) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(initialValues?.userTab || 'individual');

  const required = key =>
    validators.required(intl.formatMessage({ id: key }));
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'SignupForm.emailInvalid' })
  );

  const handleSubmit = values => {
    onCaptureValues?.(values);
    const { userTab, companyName, email, password, firstName, lastName, marketingConsent } = values;
    const isBusiness = userTab === 'business';
    const trimmedCompanyName = (companyName || '').trim();
    // Business display name is the company name.
    const displayNameMaybe =
      isBusiness && trimmedCompanyName ? { displayName: trimmedCompanyName } : {};
    onSubmit({
      email,
      password,
      firstName: (firstName || '').trim(),
      lastName: (lastName || '').trim(),
      ...displayNameMaybe,
      marketingConsent: marketingConsent === true,
      publicData: {
        userType: isBusiness ? 'business' : 'individual',
      },
      protectedData: {},
      privateData: {},
    });
  };

  const tabBtnCls = tab =>
    `flex-1 flex items-center justify-center gap-2 py-2 text-sm font-semibold uppercase tracking-wide rounded-md transition-colors ${
      activeTab === tab
        ? 'bg-background text-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground'
    }`;

  return (
    <FinalForm
      onSubmit={handleSubmit}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, invalid }) => {
        const submitDisabled = invalid || inProgress;
        return (
          <form onSubmit={handleSubmit} className="w-full">
            {/* Tab switcher */}
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1 mt-6 mb-4">
              <button
                type="button"
                className={tabBtnCls('individual')}
                onClick={() => setActiveTab('individual')}
              >
                <User className="w-4 h-4" />
                Individual
              </button>
              <button
                type="button"
                className={tabBtnCls('business')}
                onClick={() => setActiveTab('business')}
              >
                <Building2 className="w-4 h-4" />
                Business
              </button>
            </div>

            {/* Hidden field tracks active tab so submit knows the user type */}
            <Field name="userTab" initialValue={activeTab}>
              {({ input }) => <input {...input} type="hidden" value={activeTab} />}
            </Field>

            {/* Business-only: company name */}
            {activeTab === 'business' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1.5" htmlFor="bus-company">
                  Company Name
                </label>
                <FieldInput
                  name="companyName"
                  id="bus-company"
                  placeholder="Acme Construction LLC"
                />
              </div>
            )}

            {/* First / Last name */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" htmlFor="signup-firstName">
                  {activeTab === 'business' ? 'Contact First Name' : 'First Name'}
                </label>
                <FieldInput
                  name="firstName"
                  id="signup-firstName"
                  placeholder={activeTab === 'business' ? 'Jane' : 'John'}
                  validate={required('SignupForm.firstNameRequired')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" htmlFor="signup-lastName">
                  {activeTab === 'business' ? 'Contact Last Name' : 'Last Name'}
                </label>
                <FieldInput
                  name="lastName"
                  id="signup-lastName"
                  placeholder={activeTab === 'business' ? 'Smith' : 'Doe'}
                  validate={required('SignupForm.lastNameRequired')}
                />
              </div>
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" htmlFor="signup-email">
                {activeTab === 'business' ? 'Work Email Address' : 'Email Address'}
              </label>
              <FieldInput
                name="email"
                id="signup-email"
                type="email"
                autoComplete="email"
                placeholder={activeTab === 'business' ? 'jane@acmeconstruction.com' : 'john@example.com'}
                validate={validators.composeValidators(
                  required('SignupForm.emailRequired'),
                  emailValid
                )}
              />
            </div>

            {/* Password */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1.5" htmlFor="signup-password">
                Password
              </label>
              <FieldInput
                name="password"
                id="signup-password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                validate={validators.composeValidators(
                  validators.minLength(
                    intl.formatMessage(
                      { id: 'SignupForm.passwordTooShort' },
                      { minLength: validators.PASSWORD_MIN_LENGTH }
                    ),
                    validators.PASSWORD_MIN_LENGTH
                  ),
                  validators.passwordFormatValid(
                    intl.formatMessage(
                      { id: 'SignupForm.passwordRequirements' },
                      { minLength: validators.PASSWORD_MIN_LENGTH }
                    )
                  )
                )}
                hint={intl.formatMessage(
                  { id: 'SignupForm.passwordRequirements' },
                  { minLength: validators.PASSWORD_MIN_LENGTH }
                )}
              />
            </div>

            <div className={css.marketingConsentRoot}>
              <FieldCheckbox
                id="signup-marketing-consent"
                name="marketingConsent"
                textClassName={css.finePrint}
                label={intl.formatMessage({ id: 'SignupForm.marketingConsentLabel' })}
              />
            </div>

            {/* Terms (rendered by AuthenticationPage via termsAndConditions prop) */}
            {termsAndConditions}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitDisabled}
              className="w-full h-12 rounded-md bg-primary px-4 py-2 text-sm font-semibold uppercase tracking-wide text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 mt-6"
            >
              {inProgress ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Creating account…
                </span>
              ) : (
                'Continue'
              )}
            </button>

            {/* Footer links */}
            <p className="mt-4 text-center text-xs text-muted-foreground">
              By clicking continue you agree to our{' '}
              <NamedLink name="TermsOfServicePage" className="underline hover:text-foreground">
                Terms of Service
              </NamedLink>{' '}
              and{' '}
              <NamedLink name="PrivacyPolicyPage" className="underline hover:text-foreground">
                Privacy Policy
              </NamedLink>
              .
            </p>
          </form>
        );
      }}
    />
  );
};

export default CustomSignupForm;
